import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import { DeleteOutlined, EditOutlined, FormOutlined } from "@ant-design/icons";

import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useDataBlockPermissions } from "@/domains/datablocks/hooks/useDataBlockPermissions";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { OrganizationRoles } from "@/shared/utils/constants";
import { RequestStatus } from "@/domains/evidence-requests/constants";
import { DataBlockActionButtonsProps } from "@/features/datablocks/details/types";

export const DataBlockActionButtons = ({
  dataBlock,
  dataTemplate,
  setOpenDeleteModal,
  setEditRequestModal,
}: DataBlockActionButtonsProps) => {
  const { t } = useTranslation();
  const { dataBlockId } = useParams();

  const currentUser = useSelector(selectCurrentUser);
  const { canEdit, canDelete } = useDataBlockPermissions({
    dataBlock,
    dataTemplate,
    currentUser,
  });

  const deleteTooltipErrorMessage = useMemo(() => {
    if (!dataBlock || canDelete) return;

    if (dataBlock.isUsedInActiveSurvey) {
      return t("datablocks.labels.notDeletableUsedInSurvey");
    }

    if (dataBlock.request) {
      return t("datablocks.labels.notDeletableByInvitee");
    }
  }, [canDelete, dataBlock]);

  const editTooltipErrorMessage = useMemo(() => {
    if (canEdit) return;

    return t("datablocks.labels.notEditableByInvitee");
  }, [canEdit]);

  const requestEditButton = useMemo(() => {
    if (
      !currentUser ||
      !currentUser.organization ||
      !dataBlock ||
      dataBlock.request == null
    )
      return null;

    const isCurrentUserAdminOrSupervisor =
      currentUser.organizationRole &&
      [
        OrganizationRoles.ADMIN.value,
        OrganizationRoles.SUPERVISOR.value,
      ].includes(currentUser.organizationRole);
    const dataBlockBelongsToCurrentOrg =
      currentUser.organization?.id === dataBlock.organization.id;

    if (!isCurrentUserAdminOrSupervisor || !dataBlockBelongsToCurrentOrg)
      return;
    const isEditAlreadyRequested =
      dataBlock.request!.status === RequestStatus.REVISION_NEEDED.value;

    return (
      <BaseTooltip
        title={
          isEditAlreadyRequested
            ? t("datablocks.labels.editIsAlreadyRequested")
            : undefined
        }
        placement="topLeft"
      >
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
          leadingIcon={<FormOutlined />}
          disabled={isEditAlreadyRequested}
          onClick={() =>
            setEditRequestModal({
              open: true,
              request: dataBlock.request!.id,
            })
          }
        >
          {t("datablocks.labels.requestEdit")}
        </BaseButton>
      </BaseTooltip>
    );
  }, [currentUser, dataBlock]);

  return dataBlock.editable ? (
    <>
      {canDelete && (
        <BaseTooltip title={deleteTooltipErrorMessage} placement="topLeft">
          <BaseButton
            variant={BUTTON_VARIANTS.TERTIARY}
            leadingIcon={<DeleteOutlined />}
            disabled={!canDelete}
            size={BUTTON_SIZES.SMALL}
            onClick={() => setOpenDeleteModal(true)}
          >
            {t("datablocks.labels.deleteDatablock")}
          </BaseButton>
        </BaseTooltip>
      )}
      {canEdit && (
        <BaseTooltip title={editTooltipErrorMessage} placement="topLeft">
          <BaseButton
            variant={BUTTON_VARIANTS.SECONDARY}
            leadingIcon={<EditOutlined />}
            disabled={!canEdit}
            size={BUTTON_SIZES.SMALL}
            type={BUTTON_TYPES.LINK}
            to={`/evidence/${dataBlockId}/edit`}
          >
            {t("datablocks.labels.editDatablock")}
          </BaseButton>
        </BaseTooltip>
      )}
      {requestEditButton}
    </>
  ) : null;
};
